import { useCallback, useContext, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'

import { useMutation } from '@tanstack/react-query'
import { FormHandles, Scope } from '@unform/core'

import { ButtonsCancelSave } from '~/components/Buttons'
import { FeedbackContext } from '~/components/feedback'
import Loading from '~/components/Loading'
import Form from '~/components/unform/Form'
import { useErrorApiAlert } from '~/hooks/useErrorApiAlert'
import { useAPPTranslation } from '~/i18n/useAPPTranslation'
import { getCampaign } from '~/modules/retailMedia/store/campaign/actions'
import { useAppSelector } from '~/store/hooks'

import { addBannerPost } from '../../services/industry/ad'
import { BannerAddFormContent } from '../CampaignForm/elements/Banner/BannerAddFormContent'
import { deriveSegmentation } from './helpers'

export const Banner: React.FC<AddAdComponentProps<FormHandles>> = ({
  showEdit,
  formRef,
  queryParams,
  handleClose
}) => {
  const { t, tToasts } = useAPPTranslation()
  const { toast } = useContext(FeedbackContext)
  const dispatch = useDispatch()

  // copy of origin form
  const [bannersToTable, setBannersToTable] = useState([])

  // Handle error
  const [AlertErrorAdd, setErrorAdd] = useErrorApiAlert({
    title: t('rm:actions.campaign.add.banners.error.message')
  })

  const {
    campaign: {
      item: { data }
    }
  } = useAppSelector(state => state.retailMedia)

  // Atualiza os dados em caso de sucesso
  const handleSuccess = useCallback(() => {
    dispatch(
      getCampaign({
        ...queryParams,
        account_info: true,
        requestInBackground: true
      })
    )
    handleClose()
  }, [dispatch, queryParams, handleClose])

  const { mutate, isPending } = useMutation({
    mutationFn: addBannerPost,
    onSuccess: () => {
      handleSuccess()

      tToasts({
        prefix: 'rm:actions.campaign.add.banners.success',
        toastFunction: toast.success
      })
    },
    onError: error => {
      console.error(error)

      setErrorAdd(error)

      tToasts({
        prefix: 'rm:actions.campaign.add.banners.error',
        toastFunction: toast.critical
      })
    }
  })

  const segmentation = useMemo(() => deriveSegmentation(data), [data])

  const handleSubmitEdit = useCallback(() => {
    const body = {
      campaign_id: data.id,
      publisher_id: data.publisher.id,
      ad_type: data.adType,
      assets: bannersToTable.map(({ ad_size, media_url, alt_text }) => ({
        ad_size,
        media_url,
        alt_text
      }))
    }

    mutate({ body })
  }, [bannersToTable, data.id, data.publisher.id, data.adType, mutate])

  return (
    <Form ref={formRef} onSubmit={handleSubmitEdit} className="px-3">
      <Scope path="banner">
        <BannerAddFormContent
          formRef={formRef}
          type={data.adType as 'banner' | 'banner_off_site'}
          bannersToTable={bannersToTable}
          setBannersToTable={setBannersToTable}
          selectedSegmentation={segmentation}
          initialData={{ publisher_id: data?.publisher.id }}
        />
      </Scope>

      <footer className="mt-3">
        <Loading status={isPending}>{t('rm:savingChanges')}</Loading>

        <AlertErrorAdd />

        {showEdit && !isPending && (
          <ButtonsCancelSave
            onCancelClick={handleClose}
            saveButtonLabel={t('rm:campaign.create.ProductsPanel.add.button')}
            isLoading={isPending}
            isDisabled={!bannersToTable.length}
            styleOnTop="line"
          />
        )}
      </footer>
    </Form>
  )
}
